/**
 * @file WmsDashboardBody.tsx
 * @description Содержимое «Обзора склада» под плитками (phase19.31, шаг О4;
 * макет (в2) с уточнением владельца «очередь сборки слева», макет (в) для
 * телефона; состояния — §3 разбора зоны «Склад»).
 *
 * От 1024 — две колонки одинаковой ширины, блоки выровнены по верху:
 * слева «Что нужно сделать» и под ним «Очередь сборки», справа «Последние
 * движения». Очередь видна всегда (основная работа склада); пустую ленту
 * движений не показываем — тогда очередь встаёт в правую колонку, чтобы
 * под левой не было пустоты. До 1024 — одна колонка в порядке: «Что нужно
 * сделать» → «Очередь сборки» → «Последние движения».
 *
 * Состояния: склад совсем пуст — «Склад пока пуст» с переходом в приёмку;
 * выбран склад и делать нечего — «На складе «…» сегодня нет заданий и
 * проблем».
 */

import { Plus, Warehouse } from "lucide-react";
import { Link } from "react-router";

import { DASHBOARD_TEXT, quietWarehouseText } from "./dashboard-texts";
import { WmsDashboardAttention } from "./WmsDashboardAttention";
import { WmsDashboardMovements } from "./WmsDashboardMovements";
import { WmsDashboardQueue } from "./WmsDashboardQueue";
import { Button } from "../../ui/button";
import { EmptyState } from "../../ui/EmptyState";
import { LIST_SURFACE_CLASS } from "../../ui/ListStates";
import type { WmsDashboardDto } from "../../../lib/api-wms-dashboard";

interface Props {
  data: WmsDashboardDto | null;
  /** Первая загрузка: данных ещё нет. */
  loading: boolean;
  basePath: string;
  slug?: string;
  warehouseId: string;
  /** Название выбранного склада — для «пусто под складом». */
  warehouseName: string | null;
  currentUserId: string | null;
  /** Может заводить документы — кнопка в пустом состоянии. */
  canManage: boolean;
}

/** Склад пуст: ни задач, ни счётчиков, ни одного движения. */
function isWarehouseEmpty(data: WmsDashboardDto): boolean {
  const counters = Object.values(data.counters).every((value) => !value);
  return counters && data.attention.length === 0 && data.pickingQueue.length === 0 && data.recentMovements.length === 0;
}

export function WmsDashboardBody(props: Props) {
  const { data, loading, basePath, slug, warehouseId, warehouseName, currentUserId, canManage } = props;

  if (data && !warehouseId && isWarehouseEmpty(data)) {
    return (
      <EmptyState
        className={LIST_SURFACE_CLASS}
        icon={Warehouse}
        title={DASHBOARD_TEXT.emptyTitle}
        description={DASHBOARD_TEXT.emptyDescription}
        action={
          canManage ? (
            <Button asChild variant="outline">
              <Link to={`${basePath}/receipts/new`}>
                <Plus className="size-4" />
                {DASHBOARD_TEXT.newReceipt}
              </Link>
            </Button>
          ) : null
        }
      />
    );
  }

  const movements = data?.recentMovements ?? [];
  const showMovements = loading || movements.length > 0;

  const attention = (
    <WmsDashboardAttention
      items={data?.attention ?? []}
      counters={data?.counters ?? null}
      basePath={basePath}
      warehouseId={warehouseId}
      loading={loading}
      emptyText={warehouseId && warehouseName ? quietWarehouseText(warehouseName) : undefined}
    />
  );
  const queue = (
    <WmsDashboardQueue
      tasks={data?.pickingQueue ?? []}
      total={(data?.counters.pickingOpen ?? 0) + (data?.counters.pickingShortage ?? 0)}
      basePath={basePath}
      currentUserId={currentUserId}
      loading={loading}
    />
  );

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2 lg:gap-6">
      <div className="flex min-w-0 flex-col gap-4 lg:gap-6">
        {attention}
        {showMovements ? queue : null}
      </div>
      <div className="flex min-w-0 flex-col gap-4 lg:gap-6">
        {showMovements ? (
          <WmsDashboardMovements movements={movements} basePath={basePath} slug={slug} loading={loading} />
        ) : (
          queue
        )}
      </div>
    </div>
  );
}
